import * as React from 'react'
import { DateRangePicker } from 'react-dates'
import styled from 'styled-components'
import moment from 'moment'
import { tJa } from 'core/i18n'
import { jobService } from 'core/services'
import * as constants from 'static/constants'
import { Button, InputText, CheckBox } from 'components/atoms'
import { CheckBoxes } from 'components/molecules'
import GuideKindSelector from 'components/molecules/GuideKindSelector'
import TourSkillSingleSelect from 'components/molecules/TourSkill/TourSkillSingleSelect'
import {
  IJobBulkPlan,
  IWindow,
  IPost,
  ITourSkill,
  ISkill,
  IJobSlot,
  IAgentRank,
} from 'static/interfaces'
import JobSlot from './JobSlot'
import { defaultJobSlot, weekDaysChecked } from 'utils/job'
import { Tooltip } from 'components/atoms/Tooltip'
import SvgIcon from 'components/atoms/SvgIcon'

declare var window: IWindow

export interface IJobBulkPlanModalProps {
  title?: string
  post: IPost
  startTimes: string[]
  tour_skills: ITourSkill[]
  skills: ISkill[]
  agent_ranks: IAgentRank[]
}

const JobBulkPlanModal: React.FC<IJobBulkPlanModalProps> = ({
  post,
  startTimes,
  tour_skills,
  skills,
  agent_ranks,
  title,
}) => {
  const [jobBulkPlan, setJobBulkPlan] = React.useState<IJobBulkPlan>({
    id: undefined,
    post_id: post.id,
    include_no_booking: false,
    target_date_from: moment()
      .add(1, 'days')
      .format('YYYY-MM-DD'),
    target_date_to: moment()
      .add(1, 'days')
      .format('YYYY-MM-DD'),
    weekday_0: true,
    weekday_1: true,
    weekday_2: true,
    weekday_3: true,
    weekday_4: true,
    weekday_5: true,
    weekday_6: true,
    publish_start_at: moment()
      .set('hour', 5)
      .set('minute', 0)
      .set('second', 0)
      .format('YYYY-MM-DD HH:mm'),
    publish_end_at: moment()
      .set('hour', 20)
      .set('minute', 0)
      .set('second', 0)
      .format('YYYY-MM-DD HH:mm'),
  })
  const [jobBulkPlanStartTimes, setJobBulkPlanStartTimes] = React.useState<string[]>([])
  const [jobSlots, setJobSlots] = React.useState<IJobSlot[]>([defaultJobSlot()])
  const [tourSkillId, setTourSkillId] = React.useState<number | null>(tour_skills[0].id || null)
  // null = 通常ガイド
  const [agentRankId, setAgentRankId] = React.useState<number | null>(null)
  const [focusedInput, setFocusedInput] = React.useState(null)

  const specialRankOptions = React.useMemo(
    () => agent_ranks.map(rank => ({ value: String(rank.id), label: rank.name })),
    [agent_ranks]
  )

  const isOutsideRange = React.useCallback(
    (day: moment.Moment) => {
      // 今日より前は選択不可
      if (day.isBefore(moment().startOf('day'))) {
        return true
      }
      if (!jobBulkPlan.target_date_from) {
        return false
      }
      const start = moment(jobBulkPlan.target_date_from).startOf('day')
      const maxDate = start.clone().add(2, 'months')
      // 開始日から2ヶ月より後は選択不可
      return day.isAfter(maxDate)
    },
    [jobBulkPlan.target_date_from]
  )

  const handleChangeWeekDays = React.useCallback((selectedDays: Array<string | number>) => {
    setJobBulkPlan(prev => ({
      ...prev,
      weekday_0: selectedDays.includes(0),
      weekday_1: selectedDays.includes(1),
      weekday_2: selectedDays.includes(2),
      weekday_3: selectedDays.includes(3),
      weekday_4: selectedDays.includes(4),
      weekday_5: selectedDays.includes(5),
      weekday_6: selectedDays.includes(6),
    }))
  }, [])

  const handleDatesChange = React.useCallback(
    (dates: { startDate: moment.Moment | null; endDate: moment.Moment | null }) => {
      const targetDateFrom = dates.startDate?.format('YYYY-MM-DD')
      const targetDateTo = dates.endDate?.format('YYYY-MM-DD')
      // target_date_from の前日20:00を publish_end_at に自動設定
      setJobBulkPlan(prev => {
        const publishEndAt =
          dates.startDate != null
            ? dates.startDate
                .clone()
                .subtract(1, 'days')
                .set({ hour: 20, minute: 0, second: 0 })
                .format('YYYY-MM-DD HH:mm')
            : prev.publish_end_at
        return {
          ...prev,
          target_date_from: targetDateFrom,
          target_date_to: targetDateTo,
          publish_end_at: publishEndAt,
        }
      })
    },
    []
  )

  const handleSubmit = React.useCallback(async () => {
    if (!window.confirm('募集を作成します。よろしいですか？')) {
      return
    }
    try {
      const params = {
        job_bulk_plan_attributes: {
          ...jobBulkPlan,
          post_id: post.id,
          agent_rank_id: agentRankId,
          publish_start_at: moment(jobBulkPlan.publish_start_at).format('YYYY-MM-DD HH:mm'),
          publish_end_at: jobBulkPlan.publish_end_at
            ? moment(jobBulkPlan.publish_end_at).format('YYYY-MM-DD HH:mm')
            : null,
        },
        job_bulk_plan_start_times_attributes: jobBulkPlanStartTimes.map(time => ({
          start_time: time,
        })),
        job_bulk_plan_slots_attributes: jobSlots.map(slot => ({
          ...slot,
          tour_skill_id: tourSkillId,
          direct_max_limit: slot.direct_max_limit || 0,
          selection_max_limit: slot.selection_max_limit || 0,
        })),
      }
      const { jobBulkPlan: updatedJobBulkPlan, flush } = await jobService.createJobBulkPlan(params)
      window.flashMessages.addMessage({
        text: flush.message,
        type: flush.type,
      })
      window.location.reload()
    } catch (e) {
      console.error(e)
    }
  }, [jobBulkPlan, jobSlots, tourSkillId, agentRankId, jobBulkPlanStartTimes, post.id])

  return (
    <Wrapper>
      <div className="custom-modal-header">
        <div>{title}</div>
        <SvgIcon
          className="close-button"
          name="close"
          width={24}
          height={24}
          role="button"
          onClick={() => {
            window.globalModal.closeModal()
          }}
        />
      </div>
      <div className="custom-modal-body">
        <FormItem className="startTimesContainer">
          <CheckBoxes
            name="post_start_times"
            title={tJa('job_bulk_plan.post_start_times')}
            options={startTimes.map(time => ({ id: time, name: time }))}
            checked={jobBulkPlanStartTimes.map(time => ({ id: time }))}
            onChange={(selectedTimes: Array<string | number>) =>
              setJobBulkPlanStartTimes(selectedTimes as string[])
            }
          />
        </FormItem>
        <FormItem className="includeNoBookingContainer">
          <label className="title">{tJa('job_bulk_plan.include_no_booking')}</label>
          <CheckBox
            name="include_no_booking"
            label={tJa('job_bulk_plan.include_no_booking_help')}
            checked={jobBulkPlan.include_no_booking}
            showLabelInline={true}
            onChangeHandler={e =>
              setJobBulkPlan({ ...jobBulkPlan, include_no_booking: e.target.checked })
            }
          />
        </FormItem>
        <FormItem className="targetDatesContainer">
          <div className="title">{tJa('job_bulk_plan.target_dates')}</div>
          <DateRangePicker
            startDate={jobBulkPlan.target_date_from ? moment(jobBulkPlan.target_date_from) : null}
            endDate={jobBulkPlan.target_date_to ? moment(jobBulkPlan.target_date_to) : null}
            onDatesChange={handleDatesChange}
            focusedInput={focusedInput}
            onFocusChange={(currentFocusedInput: null | 'startDate' | 'endDate') =>
              setFocusedInput(currentFocusedInput)
            }
            isOutsideRange={isOutsideRange}
            numberOfMonths={1}
            hideKeyboardShortcutsPanel={true}
            displayFormat="YYYY/MM/DD"
            startDateId="target_date_from"
            endDateId="target_date_to"
          />
        </FormItem>
        <FormItem className="weekDaysContainer">
          <CheckBoxes
            name="week_days"
            title={tJa('job_bulk_plan.week_days')}
            options={constants.WEEK_DAYS_OPTIONS}
            checked={weekDaysChecked(jobBulkPlan).map(day => ({ id: day }))}
            onChange={handleChangeWeekDays}
          />
        </FormItem>
        <FormItem className="publishStartAtContainer">
          <InputText
            name="publish_start_at"
            label={tJa('job_bulk_plan.publish_start_at')}
            value={moment(jobBulkPlan.publish_start_at).format('YYYY-MM-DDTHH:mm')}
            type="datetime-local"
            onChangeHandler={e =>
              setJobBulkPlan({ ...jobBulkPlan, publish_start_at: e.target.value })
            }
          />
        </FormItem>
        <FormItem className="publishEndAtContainer">
          <InputText
            name="publish_end_at"
            label={tJa('job_bulk_plan.publish_end_at')}
            value={moment(jobBulkPlan.publish_end_at).format('YYYY-MM-DDTHH:mm')}
            type="datetime-local"
            onChangeHandler={e =>
              setJobBulkPlan({ ...jobBulkPlan, publish_end_at: e.target.value })
            }
          />
          <div className="tooltipContainer">
            <Tooltip
              position="top"
              content="締め切り日時は自動で「対象のツアー催行日」で設定した開始日の前日20:00に設定されます。変更する場合はここで設定してください。"
            >
              <SvgIcon name="help" className="icon" ariaHidden={true} role="presentation" />
            </Tooltip>
          </div>
        </FormItem>
        <FormItem className="guideKindContainer">
          <div className="title">{tJa('guide_kind.label')}</div>
          <GuideKindSelector
            name="job_bulk_plan_guide_kind"
            specialRankOptions={specialRankOptions}
            agentRankId={agentRankId}
            onChange={setAgentRankId}
          />
        </FormItem>
        <FormItem className="tourSkillContainer">
          <div className="title">{tJa('job_bulk_plan.tour_skill')}</div>
          <TourSkillSingleSelect
            className="tourSkillSelect"
            options={tour_skills.map(skill => ({ value: String(skill.id), label: skill.name }))}
            value={tourSkillId != null ? String(tourSkillId) : null}
            onChange={value => setTourSkillId(value ? Number(value) : null)}
            placeholder={tJa('generic.select_tour_skill')}
          />
        </FormItem>
        <FormItem className="jobSlotsContainer">
          <div className="title">{tJa('job_bulk_plan.job_slots')}</div>
          <div className="jobSlots">
            {jobSlots.map((jobSlot, _index) => (
              <JobSlot
                index={_index}
                key={jobSlot.id || jobSlot.client_uid || _index}
                slot={jobSlot}
                setSlots={setJobSlots}
                editting={true}
                skills={skills}
              />
            ))}
            <div className="addSlotButton">
              <button onClick={() => setJobSlots(prev => [...prev, defaultJobSlot()])}>
                <SvgIcon name="add" className="icon" ariaHidden={true} role="presentation" />
                詳細な設定を追加する
              </button>
            </div>
          </div>
        </FormItem>
      </div>
      <div className="custom-modal-footer">
        <div className="buttons">
          <Button handleClick={() => window.globalModal.closeModal2()}>
            {tJa('generic.cancel')}
          </Button>
          <Button primary={true} handleClick={handleSubmit}>
            {tJa('generic.save')}
          </Button>
        </div>
      </div>
    </Wrapper>
  )
}

const Wrapper = styled.div`
  .custom-modal-header {
    height: 64px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-weight: 600;
    font-size: 18px;
    border-bottom: 1px solid ${constants.COLORS.divider};
    padding: 0 24px;

    .close-button {
      cursor: pointer;
      width: 24px;
      height: 24px;
    }
  }

  .custom-modal-body {
    display: flex;
    flex-direction: column;
    gap: 20px;
    padding: 24px;
    max-height: calc(100dvh - (132px + 120px));
    overflow-y: auto;
    overflow-x: hidden;
    @media (max-width: ${constants.BREAKPOINT_TABLET_SMALL}px) {
      max-height: calc(100dvh - 132px);
    }
  }

  .custom-modal-footer {
    padding: 0 24px;
    border-top: 1px solid ${constants.COLORS.divider};
    .buttons {
      height: 64px;
      display: flex;
      align-items: center;
      justify-content: space-between;
      button {
        font-size: 14px;
        min-width: 120px;
      }
    }
  }
`

const FormItem = styled.div`
  display: flex;
  @media (max-width: ${constants.BREAKPOINT_TABLET_SMALL}px) {
    flex-direction: column;
    align-items: flex-start;
    .CheckBox_Main {
      flex-direction: row;
      gap: 16px;
      > div {
        margin: 0;
      }
    }
  }
  .title {
    width: 180px;
    padding: 8px 24px 8px 0;
    font-size: 14px;
    font-weight: bold;
    text-align: right;
    @media (max-width: ${constants.BREAKPOINT_TABLET_SMALL}px) {
      text-align: left;
    }
  }
  > div {
    min-height: 40px;
  }
  &.startTimesContainer {
    > div {
      margin: 0;
      > div:first-child {
        justify-content: flex-end;
        padding: 8px 24px 8px 0;
        @media (max-width: ${constants.BREAKPOINT_TABLET_SMALL}px) {
          justify-content: flex-start;
        }
      }
    }
  }
  &.includeNoBookingContainer {
    .CheckBox {
      display: flex;
      align-items: center;
    }
    .CheckBox_Wrapper {
      > div:first-child {
        justify-content: flex-end;
        padding: 0 24px 0 0;
        @media (max-width: ${constants.BREAKPOINT_TABLET_SMALL}px) {
          justify-content: flex-start;
        }
      }
      span {
        font-weight: normal;
      }
    }
  }
  &.targetDatesContainer {
    .DateRangePickerInput {
      width: 100%;
      justify-content: flex-end;
      border-color: ${constants.COLORS.divider};
      border-radius: 4px;
      .DateInput {
        width: 120px;
        padding: 2px 4px;
        border-radius: 4px;
        input.DateInput_input {
          padding: 5px 3px 3px;
          font-size: 14px;
          font-weight: normal;
          text-align: center;
        }
      }
    }
  }
  &.weekDaysContainer {
    > div {
      margin: 0;
      > div:first-child {
        justify-content: flex-end;
        padding: 8px 24px 8px 0;
        @media (max-width: ${constants.BREAKPOINT_TABLET_SMALL}px) {
          justify-content: flex-start;
        }
      }
    }
  }
  &.publishStartAtContainer,
  &.publishEndAtContainer {
    .InputText {
      > div:first-child {
        justify-content: flex-end;
        padding: 8px 24px 8px 0;
        @media (max-width: ${constants.BREAKPOINT_TABLET_SMALL}px) {
          justify-content: flex-start;
        }
      }
      input {
        width: fit-content;
        min-width: 200px;
        padding: 8px;
        border-radius: 4px;
        border: 1px solid ${constants.COLORS.divider};
        font-size: 14px;
        font-weight: normal;
        line-height: normal;
      }
    }
  }
  &.publishEndAtContainer {
    .tooltipContainer {
      display: flex;
      align-items: center;
      justify-content: center;
      margin-left: 8px;
      > div {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 20px;
        height: 20px;
      }
      .tooltipContent {
        font-size: 12px;
        max-width: 284px;
      }
      .icon {
        width: 20px;
        height: 20px;
        color: ${constants.COLORS.subText};
        cursor: pointer;
      }
    }
  }
  &.guideKindContainer,
  &.tourSkillContainer {
    align-items: center;
    .title {
      display: flex;
      align-items: center;
      justify-content: flex-end;
      padding-top: 0;
      padding-bottom: 0;
      @media (max-width: ${constants.BREAKPOINT_TABLET_SMALL}px) {
        justify-content: flex-start;
      }
    }
  }
  &.tourSkillContainer {
    .tourSkillSelect {
      min-width: 200px;
      max-width: 400px;
      font-size: 14px;
      flex-grow: 1;
    }
  }

  &.jobSlotsContainer {
    .jobSlots {
      flex: 1;
      display: flex;
      flex-direction: column;
      gap: 12px;

      .slotEdit {
        max-width: 520px;
        padding: 8px;
        font-size: 14px;
        border-radius: 8px;
        background-color: ${constants.COLORS.lightGray};
        border: 1px solid ${constants.COLORS.divider};
        transition: 0.2s background-color;
        .selectContainer {
          width: 100%;
        }
        .Select_Main {
          background-color: ${constants.COLORS.white};
        }
        &:has(.deleteSlotButton button:hover) {
          background-color: ${constants.COLORS.divider2};
        }
        .tooltipContent.left {
          @media (max-width: ${constants.BREAKPOINT_TABLET_MOBILE}px) {
            top: -120px;
            right: -56px;
            transform: none;
          }
        }
      }

      .tourSkillNumberContainer {
        .tourSkillNumber {
          label,
          input {
            height: 40px;
            @media (max-width: ${constants.BREAKPOINT_TABLET_MOBILE}px) {
              .icon {
                display: none;
              }
              &.maxLimit {
                width: 60px;
              }
            }
          }
        }
      }
      .selectContainer {
        width: 100%;
        max-width: 480px;
      }
      .addSlotButton {
        display: flex;
        align-items: center;
        height: 40px;
        button {
          display: inline-flex;
          align-items: center;
          justify-content: flex-start;
          gap: 4px;
          line-height: 20px;
          height: 20px;
          padding: 0;
          color: ${constants.COLORS.primary};
          border: none;
          background-color: none;
          background: none;
          font-size: 14px;
          transition: 0.2s color;
          cursor: pointer;
          &:hover {
            color: ${constants.COLORS.secondary};
          }
          .icon {
            width: 20px;
            height: 20px;
          }
        }
      }
    }
  }
`

export default JobBulkPlanModal
